import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectContentWithSearch, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Separator } from "./ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Badge } from "./ui/badge";
import { Textarea } from "./ui/textarea";
import { FileText, Plus, Trash2, Calculator, Calendar, User, Building, Truck, MapPin, CreditCard, ArrowLeft, Info, Check, X, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { PermissionGate } from "./ui/PermissionGate";
import { toast } from "sonner";
import { branchApiService } from '../services/branchApi';
import { Branch } from '../services/branchApi';
import { supplierApiService } from '../services/supplierApi';
import { Supplier } from '../services/supplierApi';
import { itemCategoryApiService } from '../services/itemCategoryApi';
import { ItemCategory } from '../services/itemCategoryApi';
import { rawItemApiService } from '../services/rawItemApi';
import { RawItem } from '../services/rawItemApi';
import { purchaseOrderApiService, PoHeader, PoItemDetail, PoHeaderCreateRequest, PoItemDetailCreateRequest, PoHeaderUpdateRequest, PoItemDetailUpdateRequest, LastTransaction } from '../services/purchaseOrderApi';
import { formatDateForInput, formatDateForAPI, getCurrentDate, getFutureDate } from '../utils/dateUtils';
import { useAuth } from '../contexts/AuthContext';
import { useBranch } from '../contexts/BranchContext';
import { taxRateHistoryApiService } from '../services/taxRateHistoryApi';
import { CURRENCY_CONFIG } from '../config/uiConfig';

interface LineItem {
  id: string; // Unique identifier for the line item
  rawItemId: string; // Reference to the raw item from the API
  category: string;
  itemName: string;
  purchaseRate: number;
  quantity: number;
  hsn: string;
  unit: string;
  weight: number;
  igst: number;
  cgst: number;
  sgst: number;
  totalWeight: number;
  totalTax: number;
  amount: number;
}

interface PurchaseOrderData {
  // Order Information
  poNumber: string;
  shipToBranchId: string;
  purchaseDate: string;
  poType: string;
  effectiveStartDate: string;
  effectiveEndDate: string;
  
  // Vendor Information
  vendorQuoteNumber: string;
  vendorQuoteDate: string;
  supplier: string;
  
  // Shipping & Charges
  modeOfDispatch: string;
  transportationCharges: number;
  insuranceCharges: number;
  packingCharges: number;
  installationServiceCharges: number;
  projectLeadTime: string;
  otherCharges: number;
  
  // Totals
  grandTotalTax: number;
  totalAmount: number;
  grandTotal: number;
  
  // Terms
  deliveryTerms: string;
  termConditions: string;
  paymentTerms: string;
  
  // Currency
  currency: string;
  
  // Line Items
  lineItems: LineItem[];
  status: string;
  
  // Approval fields
  approvedBy: number | null;
  signedBy: number | null;
}

export function PurchaseOrderForm() {
  // Store raw input values for rate and quantity to allow decimal typing
  const [rawInputs, setRawInputs] = useState<Record<string, { rate?: string; qty?: string }>>({});

  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { selectedBranch } = useBranch();
  
  // Determine mode from URL path
  const isViewMode = location.pathname.includes('/view');
  const isEditMode = location.pathname.includes('/edit');
  const isNewMode = !id || location.pathname.includes('/new');
  
  const [formData, setFormData] = useState<PurchaseOrderData>(() => {
    const initialData = {
      // Order Information
      poNumber: '',
      shipToBranchId: '',
      purchaseDate: getCurrentDate(),
      poType: '',
      effectiveStartDate: getCurrentDate(),
      effectiveEndDate: getFutureDate(30),
    
    // Vendor Information
    vendorQuoteNumber: '',
    vendorQuoteDate: '',
    supplier: '',
    
    // Shipping & Charges
    modeOfDispatch: '',
    transportationCharges: 0,
    insuranceCharges: 0,
    packingCharges: 0,
    installationServiceCharges: 0,
    projectLeadTime: '',
    otherCharges: 0,
    
    // Totals
    grandTotalTax: 0,
    totalAmount: 0,
    grandTotal: 0,
    
    // Terms
    deliveryTerms: '',
    termConditions: '',
    paymentTerms: '',
    
    // Currency
    currency: 'INR',
    
    // Line Items
    lineItems: [],
    status: 'Draft',
    
    // Approval fields
    approvedBy: null,
    signedBy: null
    };
    
    return initialData;
  });

  // State for branches data
  const [shipToOptions, setShipToOptions] = useState<Branch[]>([]);
  const [loadingBranches, setLoadingBranches] = useState(false);
  
  // State for suppliers data
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  
  // State for categories and raw items data
  const [categories, setCategories] = useState<ItemCategory[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [rawItems, setRawItems] = useState<RawItem[]>([]);
  const [loadingRawItems, setLoadingRawItems] = useState(false);
  // Store original PO item data for edit mode to preserve non-editable fields
  const [originalPoItems, setOriginalPoItems] = useState<PoItemDetail[]>([]);
  
  // General loading state for form operations
  const [loading, setLoading] = useState(false);
  const [poLoadState, setPoLoadState] = useState<'idle' | 'loading' | 'ok' | 'not_found'>(() =>
    id && (location.pathname.includes('/edit') || location.pathname.includes('/view')) ? 'loading' : 'idle'
  );

  // State for transaction dialog
  const [transactionDialogOpen, setTransactionDialogOpen] = useState(false);
  const [selectedRawItemId, setSelectedRawItemId] = useState<string | null>(null);
  const [selectedItemName, setSelectedItemName] = useState<string>('');
  const [lastTransactions, setLastTransactions] = useState<LastTransaction[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);

  const [poTypes] = useState([
    'Goods',
    'Services',
  ]);

  const [poStatuses] = useState([
    'DRAFT',
    'APPROVED',
    'CLOSED',
  ]);

  // Track original status for cancel functionality
  const [originalStatus, setOriginalStatus] = useState<string>('');
  const [statusLoading, setStatusLoading] = useState(false);

  // When creating or editing a PO, default Ship To to the branch selected in header
  useEffect(() => {
    if (isNewMode || isEditMode) {
      if (selectedBranch && formData.shipToBranchId !== selectedBranch.toString()) {
        setFormData(prev => ({ ...prev, shipToBranchId: selectedBranch.toString() }));
      }
    }
  }, [isNewMode, isEditMode, selectedBranch]);

  // Calculate project lead time when effective dates are available
  useEffect(() => {
    if (formData.effectiveStartDate && formData.effectiveEndDate) {
      const calculatedLeadTime = calculateProjectLeadTime(formData.effectiveStartDate, formData.effectiveEndDate);
      // Only update if calculated value is different and not empty
      if (calculatedLeadTime && calculatedLeadTime !== formData.projectLeadTime) {
        setFormData(prev => {
          // Prevent infinite loop by checking if value actually changed
          if (prev.projectLeadTime === calculatedLeadTime) return prev;
          return { ...prev, projectLeadTime: calculatedLeadTime };
        });
      }
    } else if (formData.projectLeadTime) {
      // Clear lead time if dates are missing
      setFormData(prev => ({ ...prev, projectLeadTime: '' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.effectiveStartDate, formData.effectiveEndDate]);

  // Ensure purchase date is always set to today
  useEffect(() => {
    if (isNewMode) {
      setFormData(prev => ({
        ...prev,
        purchaseDate: getCurrentDate()
      }));
    }
  }, [isNewMode]);


  const [modeOfDispatchOptions] = useState([
    'Road Transport',
    'Rail Transport',
    'Air Freight',
    'Sea Freight',
    'Courier Service',
    'Self Pickup'
  ]);





  const [units] = useState([
    'Kg', 'Grams', 'Tonnes', 'Pieces', 'Meters', 'Liters', 'Square Meters'
  ]);

  const fetchPurchaseOrder = useCallback(async (poId: string) => {
    // Don't fetch if we're in new mode
    if (isNewMode) {
      return;
    }

    const isViewOrEdit = isEditMode || isViewMode;
    try {
      if (isViewOrEdit) {
        setPoLoadState('loading');
      }
      setLoading(true);
      const [poHeader, poItems] = await Promise.all([
        purchaseOrderApiService.getPurchaseOrderById(poId),
        purchaseOrderApiService.getPoItemDetails(poId)
      ]);

      const headerBranchId = selectedBranch != null ? Number(selectedBranch) : null;
      const poBranchId = poHeader.branchId != null ? Number(poHeader.branchId) : null;
      if (
        headerBranchId == null ||
        poBranchId == null ||
        headerBranchId !== poBranchId
      ) {
        if (isViewOrEdit) {
          setPoLoadState('not_found');
        }
        return;
      }

      // Store original PO items for edit mode to preserve non-editable fields
      if (isEditMode) {
        setOriginalPoItems(poItems);
      }

      // Map PO header to form data
      setFormData(prev => ({
        ...prev,
        poNumber: poHeader.poNumber,
        shipToBranchId: poHeader.shipToBranchId?.toString() || '',
        purchaseDate: poHeader.purchaseDate ? formatDateForInput(poHeader.purchaseDate) : prev.purchaseDate,
        poType: poHeader.poType,
        effectiveStartDate: poHeader.effectiveStartDate ? formatDateForInput(poHeader.effectiveStartDate) : prev.effectiveStartDate,
        effectiveEndDate: poHeader.effectiveEndDate ? formatDateForInput(poHeader.effectiveEndDate) : prev.effectiveEndDate,
        vendorQuoteNumber: poHeader.vendorQuoteNo || '',
        vendorQuoteDate: poHeader.vendorQuoteDate ? formatDateForInput(poHeader.vendorQuoteDate) : prev.vendorQuoteDate,
        supplier: poHeader.supplier?.id?.toString() || '',
        modeOfDispatch: poHeader.modeOfDespatch,
        transportationCharges: poHeader.transportationCharges,
        insuranceCharges: poHeader.insuranceCharges,
        packingCharges: poHeader.packingCharges,
        installationServiceCharges: poHeader.installAndServiceCharges,
        projectLeadTime: poHeader.projectLeadTime,
        otherCharges: poHeader.itemCostOthCharges,
        grandTotalTax: poHeader.grandTotalTax,
        totalAmount: poHeader.totalAmount,
        grandTotal: poHeader.grandTotal,
        deliveryTerms: poHeader.deliveryTerms,
        termConditions: '', // Not available in API
        paymentTerms: poHeader.paymentTerms,
        currency: poHeader.currency || 'INR',
        status: poHeader.status,
        approvedBy: poHeader.approvedBy && poHeader.approvedBy !== 0 ? poHeader.approvedBy : null,
        signedBy: poHeader.signedBy && poHeader.signedBy !== 0 ? poHeader.signedBy : null
      }));

      // Store original status for cancel functionality
      setOriginalStatus(poHeader.status || '');

      // Map PO items to line items
      
      const mappedLineItems = poItems.map(item => {
        // Check if the item has a rawItem object (real server) or rawItemId (mock server)
        let rawItem;
        let rawItemId;
        
        if (item.rawItem) {
          // Real server format - item has nested rawItem object
          rawItem = item.rawItem;
          rawItemId = item.rawItem.id.toString();
        } else if (item.rawItemId) {
          // Mock server format - item has rawItemId field
          rawItem = rawItems.find(ri => ri.id.toString() === item.rawItemId.toString());
          rawItemId = item.rawItemId.toString();
        }
        return {
          id: item.id.toString(),
          rawItemId: rawItemId || '',
          category: rawItem?.itemCategory?.categoryName || '',
          itemName: rawItem?.itemName || item.itemDescription || '',
          purchaseRate: item.unitPrice,
          quantity: item.quantity,
          hsn: rawItem?.hsn?.hsnCode || '',
          unit: rawItem?.itemUnit || '',
          weight: rawItem?.weight || 0,
          igst: rawItem?.hsn?.currentIgstRate || 0,
          cgst: rawItem?.hsn?.currentCgstRate || 0,
          sgst: rawItem?.hsn?.currentSgstRate || 0,
          totalWeight: (rawItem?.weight || 0) * item.quantity,
          totalTax: item.taxAmount,
          amount: item.totalAmount
        };
      });
      
      setFormData(prev => ({ ...prev, lineItems: mappedLineItems }));

      if (isViewOrEdit) {
        setPoLoadState('ok');
      }
    } catch (error) {
      // console.error('Error fetching purchase order:', error);
      if (isViewOrEdit) {
        setPoLoadState('not_found');
      }
      toast.error('Failed to fetch purchase order details');
    } finally {
      setLoading(false);
    }
  }, [rawItems, isNewMode, isEditMode, isViewMode, selectedBranch]);

  // Fetch branches on component mount
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        setLoadingBranches(true);
        const branches = await branchApiService.getBranches();
        setShipToOptions(branches);
      } catch (error) {
        // console.error('Failed to fetch branches:', error);
        toast.error('Failed to fetch branches');
      } finally {
        setLoadingBranches(false);
      }
    };

    fetchBranches();
  }, []);

  // Fetch suppliers on component mount
  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        setLoadingSuppliers(true);
        // Use the same method as SupplierListingPage for consistency
        const response = await supplierApiService.getSuppliersData(1, 100); // Get up to 100 suppliers
        setSuppliers(response.suppliers);
      } catch (error) {
        // console.error('Failed to fetch suppliers:', error);
        toast.error('Failed to fetch suppliers');
        setSuppliers([]);
      } finally {
        setLoadingSuppliers(false);
      }
    };

    fetchSuppliers();
  }, []);



  // Fetch categories on component mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoadingCategories(true);
        const response = await itemCategoryApiService.getItemCategories();
        // Handle different response formats
        let categoriesData: ItemCategory[] = [];
        if (response && Array.isArray(response)) {
          categoriesData = response;
        } else if (response && typeof response === 'object' && 'data' in response) {
          categoriesData = (response as any).data || [];
        } else if (response && typeof response === 'object' && 'success' in response && 'data' in response) {
          categoriesData = (response as any).data || [];
        } else {
          // console.warn('Unexpected categories API response format:', response);
          categoriesData = [];
        }
        setCategories(categoriesData);
      } catch (error) {
        // console.error('Failed to fetch categories:', error);
        toast.error('Failed to fetch categories');
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchCategories();
  }, []);

  // Fetch raw items on component mount
  useEffect(() => {
    const fetchRawItems = async () => {
      try {
        setLoadingRawItems(true);
        const response = await rawItemApiService.getRawItems();
        // Handle different response formats
        let rawItemsData: RawItem[] = [];
        if (response && Array.isArray(response)) {
          rawItemsData = response;
        } else if (response && typeof response === 'object' && 'data' in response) {
          rawItemsData = (response as any).data || [];
        } else if (response && typeof response === 'object' && 'success' in response && 'data' in response) {
          rawItemsData = (response as any).data || [];
        } else {
          // console.warn('Unexpected raw items API response format:', response);
          rawItemsData = [];
        }
        setRawItems(rawItemsData);
      } catch (error) {
        // console.error('Failed to fetch raw items:', error);
        toast.error('Failed to fetch raw items');
      } finally {
        setLoadingRawItems(false);
      }
    };

    fetchRawItems();
  }, []);

  // Load existing PO data when editing or viewing
  useEffect(() => {
    // Only fetch data if we have an ID and we're in edit or view mode (not new mode)
    if (id && (isEditMode || isViewMode) && !isNewMode && rawItems.length > 0) {
      fetchPurchaseOrder(id);
    }
  }, [id, isEditMode, isViewMode, isNewMode, rawItems, selectedBranch, fetchPurchaseOrder]);


  // Calculate project lead time from effective dates
  const calculateProjectLeadTime = (startDate: string, endDate: string): string => {
    if (!startDate || !endDate) return '';
    
    try {
      const start = new Date(startDate);
      const end = new Date(endDate);
      
      if (isNaN(start.getTime()) || isNaN(end.getTime())) return '';
      if (end < start) return '';
      
      const diffTime = end.getTime() - start.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays < 0) return '';
      
      if (diffDays === 0) return '0 days';
      if (diffDays === 1) return '1 day';
      if (diffDays < 7) return `${diffDays} days`;
      if (diffDays < 30) {
        const weeks = Math.floor(diffDays / 7);
        const remainingDays = diffDays % 7;
        if (remainingDays === 0) {
          return weeks === 1 ? '1 week' : `${weeks} weeks`;
        }
        return `${weeks} week${weeks > 1 ? 's' : ''} ${remainingDays} day${remainingDays > 1 ? 's' : ''}`;
      }
      if (diffDays < 365) {
        const months = Math.floor(diffDays / 30);
        const remainingDays = diffDays % 30;
        if (remainingDays === 0) {
          return months === 1 ? '1 month' : `${months} months`;
        }
        const weeks = Math.floor(remainingDays / 7);
        if (weeks === 0) {
          return `${months} month${months > 1 ? 's' : ''} ${remainingDays} day${remainingDays > 1 ? 's' : ''}`;
        }
        return `${months} month${months > 1 ? 's' : ''} ${weeks} week${weeks > 1 ? 's' : ''}`;
      }
      const years = Math.floor(diffDays / 365);
      const remainingDays = diffDays % 365;
      const months = Math.floor(remainingDays / 30);
      if (months === 0) {
        return years === 1 ? '1 year' : `${years} years`;
      }
      return `${years} year${years > 1 ? 's' : ''} ${months} month${months > 1 ? 's' : ''}`;
    } catch (error) {
      return '';
    }
  };

  // Ensure purchase date is always set to today
  useEffect(() => {
    if (isNewMode) {
      setFormData(prev => ({
        ...prev,
        purchaseDate: getCurrentDate()
      }));
    }
  }, [isNewMode]);

  const handleInputChange = (field: keyof PurchaseOrderData, value: string | number) => {
    // Purchase date should always be today's date - prevent changes
    if (field === 'purchaseDate' && typeof value === 'string' && isNewMode) {
      const today = getCurrentDate();
      if (value !== today) {
        toast.error('Purchase date must be today\'s date');
        // Reset to today's date
        setFormData(prev => ({
          ...prev,
          purchaseDate: getCurrentDate()
        }));
        return;
      }
    }
    
    // Validate effective start date - must be between today and today + 7 days (only for new POs)
    if (field === 'effectiveStartDate' && typeof value === 'string' && value && isNewMode) {
      const selectedDate = new Date(value + 'T00:00:00');
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const maxDate = new Date(today);
      maxDate.setDate(maxDate.getDate() + 7);
      
      if (selectedDate < today) {
        toast.error('Effective start date cannot be in the past');
        // Reset to today
        setFormData(prev => ({
          ...prev,
          effectiveStartDate: getCurrentDate()
        }));
        return;
      }
      
      if (selectedDate > maxDate) {
        toast.error('Effective start date cannot be more than 7 days from today');
        // Reset to maximum allowed date
        setFormData(prev => ({
          ...prev,
          effectiveStartDate: getFutureDate(7)
        }));
        return;
      }
    }
    
    // Validate effective end date - must be today or future (no upper limit) (only for new POs)
    if (field === 'effectiveEndDate' && typeof value === 'string' && value && isNewMode) {
      const selectedDate = new Date(value + 'T00:00:00');
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (selectedDate < today) {
        toast.error('Effective end date cannot be in the past');
        // Reset to today
        setFormData(prev => ({
          ...prev,
          effectiveEndDate: getCurrentDate()
        }));
        return;
      }
      // No upper limit - allows any future date
    }
    
    // Validate vendor quote date - can be past but not future
    if (field === 'vendorQuoteDate' && typeof value === 'string' && value) {
      const selectedDate = new Date(value);
      const today = new Date();
      today.setHours(23, 59, 59, 999); // End of today
      
      if (selectedDate > today) {
        toast.error('Vendor quote date cannot be in the future');
        return;
      }
    }
    
    setFormData(prev => {
      const updated = {
        ...prev,
        [field]: value
      };
      
      // Auto-calculate project lead time when effective dates change
      if (field === 'effectiveStartDate' || field === 'effectiveEndDate') {
        const startDate = field === 'effectiveStartDate' ? value as string : updated.effectiveStartDate;
        const endDate = field === 'effectiveEndDate' ? value as string : updated.effectiveEndDate;
        updated.projectLeadTime = calculateProjectLeadTime(startDate, endDate);
      }
      
      return updated;
    });
    
    // Recalculate tax rates if supplier, ship-to, or purchase date changes
    if (field === 'supplier' || field === 'shipToBranchId' || field === 'purchaseDate') {
      setTimeout(() => {
        updateTaxRatesForAllItems().catch(console.error);
      }, 100);
    }
    
    setTimeout(calculateTotals, 100);
  };

  // Update tax rates for all line items based on current state and HSN
  const updateTaxRatesForAllItems = async () => {
    for (const item of formData.lineItems) {
      const rawItem = rawItems.find(r => r.id.toString() === item.rawItemId);
      const hsnId = rawItem?.hsn?.id;
      const taxRates = await getTaxRates(item.hsn, hsnId);
      updateLineItem(item.id, 'igst', taxRates.igst);
      updateLineItem(item.id, 'cgst', taxRates.cgst);
      updateLineItem(item.id, 'sgst', taxRates.sgst);
    }
  };

  const addLineItem = async () => {
    // Get appropriate tax rates based on current state (no HSN yet, so use defaults)
    const taxRates = await getTaxRates();
    
    const newItem: LineItem = {
      id: `line_${Date.now()}_${Math.random()}`, // Unique identifier for the line item
      rawItemId: '', // Will be set when an item is selected from dropdown
      category: '',
      itemName: '',
      purchaseRate: 0,
      quantity: 0,
      hsn: '',
      unit: '',
      weight: 0,
      igst: taxRates.igst,
      cgst: taxRates.cgst,
      sgst: taxRates.sgst,
      totalWeight: 0,
      totalTax: 0,
      amount: 0
    };
    
    setFormData(prev => ({
      ...prev,
      lineItems: [...prev.lineItems, newItem]
    }));
  };

  const removeLineItem = (itemId: string) => {
    setFormData(prev => ({
      ...prev,
      lineItems: prev.lineItems.filter(item => item.id !== itemId)
    }));
    setTimeout(calculateTotals, 100);
  };

  const updateLineItem = (itemId: string, field: keyof LineItem, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      lineItems: prev.lineItems.map(item => {
        if (item.id === itemId) {
          const updatedItem = { ...item, [field]: value };
          
          // Calculate derived values
          if (field === 'quantity' || field === 'purchaseRate' || field === 'weight') {
            const rate = field === 'purchaseRate' ? Number(value) : updatedItem.purchaseRate;
            const qty = field === 'quantity' ? Number(value) : updatedItem.quantity;
            const weight = field === 'weight' ? Number(value) : updatedItem.weight;
            
            updatedItem.totalWeight = weight * qty;
            updatedItem.amount = rate * qty;
            // GST calculation: For interstate use IGST only, for intrastate use CGST + SGST only
            const sameState = isSameState();
            if (sameState) {
              updatedItem.totalTax = (updatedItem.amount * (updatedItem.cgst + updatedItem.sgst)) / 100;
            } else {
              updatedItem.totalTax = (updatedItem.amount * updatedItem.igst) / 100;
            }
          }
          
          if (field === 'igst' || field === 'cgst' || field === 'sgst') {
            // GST calculation: For interstate use IGST only, for intrastate use CGST + SGST only
            const sameState = isSameState();
            if (sameState) {
              updatedItem.totalTax = (updatedItem.amount * (updatedItem.cgst + updatedItem.sgst)) / 100;
            } else {
              updatedItem.totalTax = (updatedItem.amount * updatedItem.igst) / 100;
            }
          }
          
          return updatedItem;
        }
        return item;
      })
    }));
    setTimeout(calculateTotals, 100);
  };

  // Get filtered raw items based on selected category, excluding already selected items
  const getFilteredRawItems = (selectedCategory: string, currentItemId?: string) => {
    if (!selectedCategory) return [];
    
    // Get all currently selected raw item IDs (excluding the current item being edited)
    const selectedRawItemIds = formData.lineItems
      .filter(item => item.rawItemId && item.id !== currentItemId)
      .map(item => item.rawItemId);
    
    return rawItems.filter(item => 
      item.itemCategory?.categoryName === selectedCategory && 
      !selectedRawItemIds.includes(item.id.toString())
    );
  };

  const selectItemForLine = async (itemId: string, selectedItemId: string) => {
    const selectedItem = rawItems.find(item => item.id.toString() === selectedItemId);
    if (selectedItem) {
      // Set the raw item ID reference
      updateLineItem(itemId, 'rawItemId', selectedItem.id.toString());
      updateLineItem(itemId, 'itemName', selectedItem.itemName);
      updateLineItem(itemId, 'category', selectedItem.itemCategory?.categoryName || '');
      updateLineItem(itemId, 'hsn', selectedItem.hsn?.hsnCode || '');
      updateLineItem(itemId, 'unit', selectedItem.itemUnit);
      // Auto-populate additional fields with available data and defaults
      updateLineItem(itemId, 'weight', selectedItem.weight || 0);
      
      // Apply appropriate tax rates based on state and HSN using API
      const hsnId = selectedItem.hsn?.id;
      const taxRates = await getTaxRates(selectedItem.hsn?.hsnCode, hsnId);
      updateLineItem(itemId, 'igst', taxRates.igst);
      updateLineItem(itemId, 'cgst', taxRates.cgst);
      updateLineItem(itemId, 'sgst', taxRates.sgst);
    }
  };

  // Check if supplier and ship-to are in the same state
  const isSameState = () => {
    if (!formData.supplier || !formData.shipToBranchId) return false;
    
    const selectedSupplier = suppliers.find(s => s.id.toString() === formData.supplier);
    const selectedBranch = shipToOptions.find(b => b.id === parseInt(formData.shipToBranchId));
    
    if (!selectedSupplier || !selectedBranch) return false;
    
    return selectedSupplier.stateName === selectedBranch.stateName;
  };

  // Get appropriate tax rates based on state and HSN
  const getTaxRates = async (hsnCode?: string, hsnId?: number) => {
    const sameState = isSameState();
    
    if (!hsnCode) {
      // Default rates if no HSN is selected
      if (sameState) {
        return { igst: 0, cgst: 9, sgst: 9 };
      } else {
        return { igst: 18, cgst: 0, sgst: 0 };
      }
    }
    
    // If hsnId is provided, call the API to get applicable tax rate
    if (hsnId && formData.purchaseDate) {
      try {
        // Format date for API (YYYY-MM-DD)
        const dateForAPI = formData.purchaseDate.split('T')[0] || formData.purchaseDate;
        const applicableRate = await taxRateHistoryApiService.getApplicableTaxRate(hsnId, dateForAPI);
        
        if (sameState) {
          // Same state: use CGST and SGST from API, IGST = 0
          return {
            igst: 0,
            cgst: applicableRate.cgstRate,
            sgst: applicableRate.sgstRate
          };
        } else {
          // Different state: use IGST from API, CGST and SGST = 0
          return {
            igst: applicableRate.igstRate,
            cgst: 0,
            sgst: 0
          };
        }
      } catch (error) {
        // console.error('Error fetching applicable tax rate:', error);
        // Fallback to HSN data from rawItems if API call fails
      }
    }
    
    // Fallback: Find the HSN code to get dynamic rates from rawItems
    const hsnData = rawItems.find(item => item.hsn?.hsnCode === hsnCode)?.hsn;
    
    if (!hsnData) {
      // Fallback to default rates if HSN not found
      if (sameState) {
        return { igst: 0, cgst: 9, sgst: 9 };
      } else {
        return { igst: 18, cgst: 0, sgst: 0 };
      }
    }
    
    if (sameState) {
      // Same state: use CGST and SGST from HSN, IGST = 0
      return {
        igst: 0,
        cgst: hsnData.currentCgstRate,
        sgst: hsnData.currentSgstRate
      };
    } else {
      // Different state: use IGST from HSN, CGST and SGST = 0
      return {
        igst: hsnData.currentIgstRate,
        cgst: 0,
        sgst: 0
      };
    }
  };

  // Get HSN source info for display
  const getHsnSourceInfo = (hsnCode?: string) => {
    if (!hsnCode) return { source: 'default', rates: 'Default rates applied' };
    
    const hsnData = rawItems.find(item => item.hsn?.hsnCode === hsnCode)?.hsn;
    if (!hsnData) return { source: 'default', rates: 'Default rates applied' };
    
    const sameState = isSameState();
    if (sameState) {
      return { 
        source: 'hsn', 
        rates: `HSN: CGST ${hsnData.currentCgstRate}%, SGST ${hsnData.currentSgstRate}%` 
      };
    } else {
      return { 
        source: 'hsn', 
        rates: `HSN: IGST ${hsnData.currentIgstRate}%` 
      };
    }
  };

  // Handle opening transaction dialog
  const handleShowTransactions = async (rawItemId: string, itemName: string) => {
    if (!rawItemId) return;
    
    setSelectedRawItemId(rawItemId);
    setSelectedItemName(itemName);
    setTransactionDialogOpen(true);
    setLoadingTransactions(true);
    setLastTransactions([]);

    try {
      const response = await purchaseOrderApiService.getLastTransactions(rawItemId, selectedBranch || undefined);
      if (response.success && response.data) {
        setLastTransactions(response.data);
      } else {
        toast.error('Failed to fetch transaction history');
        setLastTransactions([]);
      }
    } catch (error) {
      // console.error('Error fetching transactions:', error);
      toast.error('Failed to fetch transaction history');
      setLastTransactions([]);
    } finally {
      setLoadingTransactions(false);
    }
  };

  // Handle category change - clear item selection and reset fields
  const handleCategoryChange = (itemId: string, categoryName: string) => {
    updateLineItem(itemId, 'category', categoryName);
    // Clear item selection and reset fields when category changes
    updateLineItem(itemId, 'rawItemId', '');
    updateLineItem(itemId, 'itemName', '');
    updateLineItem(itemId, 'hsn', '');
    updateLineItem(itemId, 'unit', '');
    updateLineItem(itemId, 'weight', 0);
    
    // Apply appropriate tax rates based on state (no HSN yet, so use defaults)
    getTaxRates().then(taxRates => {
      updateLineItem(itemId, 'igst', taxRates.igst);
      updateLineItem(itemId, 'cgst', taxRates.cgst);
      updateLineItem(itemId, 'sgst', taxRates.sgst);
    });
  };

  const calculateTotals = () => {
    setFormData(prev => {
      const subtotal = prev.lineItems.reduce((sum, item) => sum + item.amount, 0);
      const totalTax = prev.lineItems.reduce((sum, item) => sum + item.totalTax, 0);
      const totalCharges = prev.transportationCharges + prev.insuranceCharges + 
                          prev.packingCharges + prev.installationServiceCharges + prev.otherCharges;
      const grandTotal = subtotal + totalTax + totalCharges;

      return {
        ...prev,
        totalAmount: subtotal,
        grandTotalTax: totalTax,
        grandTotal: grandTotal
      };
    });
  };

  const handleSaveStatus = async () => {
    if (!id || !formData.status || !user?.id) {
      toast.error('Cannot save status');
      return;
    }

    try {
      setStatusLoading(true);
      
      // Use the dedicated status update endpoint
      await purchaseOrderApiService.updatePoStatus(id, formData.status, user.id);
      
      setOriginalStatus(formData.status);
      toast.success('Status updated successfully');
    } catch (error) {
      // console.error('Error updating status:', error);
      toast.error('Failed to update status');
    } finally {
      setStatusLoading(false);
    }
  };

  const handleCancelStatus = () => {
    setFormData(prev => ({
      ...prev,
      status: originalStatus
    }));
    toast.info('Status change cancelled');
  };

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!user?.id) {
      toast.error('User information is required. Please log in again.');
      return;
    }

    if (!selectedBranch) {
      toast.error('Please select a branch from the header to create a purchase order.');
      return;
    }
    
    if (formData.lineItems.length === 0) {
      toast.error('Please add at least one line item');
      return;
    }

          // Validate that all line items have a valid raw item ID
      const invalidItems = formData.lineItems.filter(item => !item.rawItemId);
      if (invalidItems.length > 0) {
        toast.error('Please select items from the dropdown for all line items');
        return;
      }

      // Validate that shipTo (branch) is selected
      if (!formData.shipToBranchId) {
        toast.error('Please select a Ship To branch');
        return;
      }

      // Validate that supplier is selected
      if (!formData.supplier) {
        toast.error('Please select a supplier');
        return;
      }

      // Validate effective start date - must be between today and today + 7 days (only for new POs)
      if (formData.effectiveStartDate && isNewMode) {
        const effectiveStartDate = new Date(formData.effectiveStartDate + 'T00:00:00');
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const maxStartDate = new Date(today);
        maxStartDate.setDate(maxStartDate.getDate() + 7);
        
        if (effectiveStartDate < today) {
          toast.error('Effective start date cannot be in the past');
          return;
        }
        
        if (effectiveStartDate > maxStartDate) {
          toast.error('Effective start date cannot be more than 7 days from today');
          return;
        }
      }

      // Validate effective end date - must be today or future (only for new POs)
      if (formData.effectiveEndDate && isNewMode) {
        const effectiveEndDate = new Date(formData.effectiveEndDate + 'T00:00:00');
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        if (effectiveEndDate < today) {
          toast.error('Effective end date cannot be in the past');
          return;
        }
      }

      // Validate vendor quote date - can be past but not future
      if (formData.vendorQuoteDate) {
        const vendorQuoteDate = new Date(formData.vendorQuoteDate);
        const today = new Date();
        today.setHours(23, 59, 59, 999);
        if (vendorQuoteDate > today) {
          toast.error('Vendor quote date cannot be in the future');
          return;
        }
      }

      try {
        setLoading(true);
        
        if (isEditMode && id) {
        // Update existing PO
        const poHeaderData: PoHeaderUpdateRequest = {
          id: parseInt(id),
          poNumber: formData.poNumber,
          purchaseDate: formData.purchaseDate,
          expectedDeliveryDate: formData.effectiveEndDate,
          actualDeliveryDate: '',
          paymentTerms: formData.paymentTerms,
          deliveryTerms: formData.deliveryTerms,
          currency: formData.currency || 'INR',
          exchangeRate: null,
          status: formData.status,
          effectiveStartDate: formData.effectiveStartDate,
          effectiveEndDate: formData.effectiveEndDate,
          modeOfDespatch: formData.modeOfDispatch,
          transportationCharges: formData.transportationCharges,
          insuranceCharges: formData.insuranceCharges,
          packingCharges: formData.packingCharges,
          installAndServiceCharges: formData.installationServiceCharges,
          projectLeadTime: formData.projectLeadTime,
          itemCostOthCharges: formData.otherCharges,
          grandTotalTax: formData.grandTotalTax,
          totalAmount: formData.totalAmount,
          grandTotal: formData.grandTotal,
          amendmentRevisionNumber: '',
          amendmentChangeDate: '',
          amendmentReason: '',
          approvedDate: '',
          approvedBy: formData.approvedBy,
          signedBy: formData.signedBy,
          poType: formData.poType,
          priority: 'Normal',
          sourceType: 'Manual',
          vendorQuoteNo: formData.vendorQuoteNumber,
          vendorQuoteDate: formData.vendorQuoteDate,
          isActive: true,
          branchId: selectedBranch || 0, // Use selected branch from header
          shipToBranchId: parseInt(formData.shipToBranchId),
          createdBy: user?.id,
          updatedBy: user?.id,
          supplierId: parseInt(formData.supplier)
        };

        await purchaseOrderApiService.updatePurchaseOrder(id, poHeaderData);

        // Handle item deletions, updates, and creations
        if (isEditMode && originalPoItems.length > 0) {
          // Find items that were deleted (exist in original but not in current formData)
          const currentItemIds = new Set(formData.lineItems.map(item => item.id));
          const deletedItems = originalPoItems.filter(origItem => {
            const origId = origItem.id.toString();
            return !currentItemIds.has(origId);
          });

          // Delete removed items
          for (const deletedItem of deletedItems) {
            try {
              await purchaseOrderApiService.deletePoItemDetail(deletedItem.id.toString());
            } catch (error) {
              // console.error(`Error deleting item ${deletedItem.id}:`, error);
              // Continue with other operations even if one deletion fails
            }
          }
        }

        // Update or create PO items
        for (const item of formData.lineItems) {
          // Check if item is new (ID starts with "line_" or is not a valid number)
          const isNewItem = item.id.startsWith('line_') || isNaN(parseInt(item.id));
          
          if (isNewItem) {
            // Create new item
            // Calculate taxPercentage correctly: same state uses CGST+SGST, interstate uses IGST
            const sameState = isSameState();
            const taxPercentage = sameState ? (item.cgst + item.sgst) : item.igst;
            
            const poItemData: PoItemDetailCreateRequest = {
              quantity: item.quantity,
              receivedQuantity: 0,
              unitOfMeasure: item.unit,
              itemDescription: item.itemName,
              unitPrice: item.purchaseRate,
              discountPercentage: 0,
              discountAmount: 0,
              taxPercentage: taxPercentage,
              taxAmount: item.totalTax,
              totalAmount: item.quantity * item.purchaseRate, // totalAmount = qty * unit_price (base amount without tax)
              notes: '',
              poHeaderId: parseInt(id),
              rawItemId: parseInt(item.rawItemId)
            };

            await purchaseOrderApiService.createPoItemDetail(poItemData);
          } else {
            // Update existing item - in edit mode, only update rate and quantity, preserve other fields
            const originalItem = originalPoItems.find(orig => orig.id.toString() === item.id);
            // Calculate taxPercentage correctly: same state uses CGST+SGST, interstate uses IGST
            const sameState = isSameState();
            const taxPercentage = sameState ? (item.cgst + item.sgst) : item.igst;
            
            const poItemData: PoItemDetailUpdateRequest = {
              id: parseInt(item.id),
              quantity: item.quantity, // Only editable field
              receivedQuantity: originalItem?.receivedQuantity || 0,
              unitOfMeasure: originalItem?.unitOfMeasure || item.unit,
              itemDescription: originalItem?.itemDescription || item.itemName,
              unitPrice: item.purchaseRate, // Only editable field
              discountPercentage: originalItem?.discountPercentage || 0,
              discountAmount: originalItem?.discountAmount || 0,
              taxPercentage: taxPercentage,
              taxAmount: item.totalTax, // Use recalculated tax
              totalAmount: item.quantity * item.purchaseRate, // totalAmount = qty * unit_price (base amount without tax)
              notes: originalItem?.notes || '',
              poHeaderId: parseInt(id),
              rawItemId: originalItem?.rawItemId || parseInt(item.rawItemId)
            };

            await purchaseOrderApiService.updatePoItemDetail(item.id, poItemData);
          }
        }

        toast.success('Purchase order updated successfully!');
        navigate('/purchase-orders');
      } else {
        // Create new PO
        const poHeaderData: PoHeaderCreateRequest = {
          poNumber: '', // Will be generated on server side
          purchaseDate: getCurrentDate(), // Always use today's date
          expectedDeliveryDate: formData.effectiveEndDate,
          actualDeliveryDate: '',
          paymentTerms: formData.paymentTerms,
          deliveryTerms: formData.deliveryTerms,
          currency: formData.currency || 'INR',
          exchangeRate: null,
          status: formData.status,
          effectiveStartDate: formData.effectiveStartDate,
          effectiveEndDate: formData.effectiveEndDate,
          modeOfDespatch: formData.modeOfDispatch,
          transportationCharges: formData.transportationCharges,
          insuranceCharges: formData.insuranceCharges,
          packingCharges: formData.packingCharges,
          installAndServiceCharges: formData.installationServiceCharges,
          projectLeadTime: formData.projectLeadTime,
          itemCostOthCharges: formData.otherCharges,
          grandTotalTax: formData.grandTotalTax,
          totalAmount: formData.totalAmount,
          grandTotal: formData.grandTotal,
          amendmentRevisionNumber: '',
          amendmentChangeDate: '',
          amendmentReason: '',
          approvedDate: '',
          approvedBy: formData.approvedBy,
          signedBy: formData.signedBy,
          poType: formData.poType,
          priority: 'Normal',
          sourceType: 'Manual',
          vendorQuoteNo: formData.vendorQuoteNumber,
          vendorQuoteDate: formData.vendorQuoteDate,
          isActive: true,
          branchId: selectedBranch || 0, // Use selected branch from header
          shipToBranchId: parseInt(formData.shipToBranchId),
          createdBy: user?.id,
          updatedBy: user?.id,
          supplierId: parseInt(formData.supplier)
        };

        // console.log('Creating PO with data:', poHeaderData);
        // console.log('formData.shipToBranchId:', formData.shipToBranchId);
        // console.log('branchId:', parseInt(formData.shipToBranchId));

        const createdPO = await purchaseOrderApiService.createPurchaseOrder(poHeaderData);

        // Create PO items
        for (const item of formData.lineItems) {
          // console.log(item);
          // Calculate taxPercentage correctly: same state uses CGST+SGST, interstate uses IGST
          const sameState = isSameState();
          const taxPercentage = sameState ? (item.cgst + item.sgst) : item.igst;
          
          const poItemData: PoItemDetailCreateRequest = {
            quantity: item.quantity,
            receivedQuantity: 0,
            unitOfMeasure: item.unit,
            itemDescription: item.itemName,
            unitPrice: item.purchaseRate,
            discountPercentage: 0,
            discountAmount: 0,
            taxPercentage: taxPercentage,
            taxAmount: item.totalTax,
            totalAmount: item.quantity * item.purchaseRate, // totalAmount = qty * unit_price (base amount without tax)
            notes: '',
            poHeaderId: createdPO.id,
            rawItemId: parseInt(item.rawItemId)
          };

          await purchaseOrderApiService.createPoItemDetail(poItemData);
        }

        toast.success('Purchase order created successfully!');
        navigate('/purchase-orders');
      }
    } catch (error) {
      // console.error('Error saving purchase order:', error);
      toast.error('Failed to save purchase order');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitForApproval = async () => {
    if (!user?.id) {
      toast.error('User information is required. Please log in again.');
      return;
    }

    if (!selectedBranch) {
      toast.error('Please select a branch from the header to submit the purchase order.');
      return;
    }
    
    if (formData.lineItems.length === 0) {
      toast.error('Please add at least one line item');
      return;
    }

          // Validate that all line items have a valid raw item ID
      const invalidItems = formData.lineItems.filter(item => !item.rawItemId);
      if (invalidItems.length > 0) {
        toast.error('Please select items from the dropdown for all line items');
        return;
      }

      // Validate that shipTo (branch) is selected
      if (!formData.shipToBranchId) {
        toast.error('Please select a Ship To branch');
        return;
      }

      // Validate that supplier is selected
      if (!formData.supplier) {
        toast.error('Please select a supplier');
        return;
      }

      try {
        setLoading(true);
        
        // First save the PO with updated status
        const updatedFormData = { ...formData, status: 'Pending Approval' };
      
      if (isEditMode && id) {
        // Update existing PO with new status
        const poHeaderData: PoHeaderUpdateRequest = {
          id: parseInt(id),
          poNumber: updatedFormData.poNumber,
          purchaseDate: updatedFormData.purchaseDate,
          expectedDeliveryDate: updatedFormData.effectiveEndDate,
          actualDeliveryDate: '',
          paymentTerms: updatedFormData.paymentTerms,
          deliveryTerms: updatedFormData.deliveryTerms,
          currency: updatedFormData.currency || 'INR',
          exchangeRate: null,
          status: updatedFormData.status,
          effectiveStartDate: updatedFormData.effectiveStartDate,
          effectiveEndDate: updatedFormData.effectiveEndDate,
          modeOfDespatch: updatedFormData.modeOfDispatch,
          transportationCharges: updatedFormData.transportationCharges,
          insuranceCharges: updatedFormData.insuranceCharges,
          packingCharges: updatedFormData.packingCharges,
          installAndServiceCharges: updatedFormData.installationServiceCharges,
          projectLeadTime: updatedFormData.projectLeadTime,
          itemCostOthCharges: updatedFormData.otherCharges,
          grandTotalTax: updatedFormData.grandTotalTax,
          totalAmount: updatedFormData.totalAmount,
          grandTotal: updatedFormData.grandTotal,
          amendmentRevisionNumber: '',
          amendmentChangeDate: '',
          amendmentReason: '',
          approvedDate: '',
          approvedBy: updatedFormData.approvedBy,
          signedBy: updatedFormData.signedBy,
          poType: updatedFormData.poType,
          priority: 'Normal',
          sourceType: 'Manual',
          vendorQuoteNo: updatedFormData.vendorQuoteNumber,
          vendorQuoteDate: updatedFormData.vendorQuoteDate,
          isActive: true,
          branchId: selectedBranch || 0, // Use selected branch from header
          shipToBranchId: parseInt(updatedFormData.shipToBranchId),
          createdBy: user?.id,
          updatedBy: user?.id,
          supplierId: parseInt(updatedFormData.supplier)
        };

        await purchaseOrderApiService.updatePurchaseOrder(id, poHeaderData);
        setFormData(updatedFormData);
        toast.success('Purchase order submitted for approval!');
        navigate('/purchase-orders');
      } else {
        // Create new PO with Pending Approval status
        const poHeaderData: PoHeaderCreateRequest = {
          poNumber: '', // Will be generated on server side
          purchaseDate: updatedFormData.purchaseDate,
          expectedDeliveryDate: updatedFormData.effectiveEndDate,
          actualDeliveryDate: '',
          paymentTerms: updatedFormData.paymentTerms,
          deliveryTerms: updatedFormData.deliveryTerms,
          currency: updatedFormData.currency || 'INR',
          exchangeRate: null,
          status: updatedFormData.status,
          effectiveStartDate: updatedFormData.effectiveStartDate,
          effectiveEndDate: updatedFormData.effectiveEndDate,
          modeOfDespatch: updatedFormData.modeOfDispatch,
          transportationCharges: updatedFormData.transportationCharges,
          insuranceCharges: updatedFormData.insuranceCharges,
          packingCharges: updatedFormData.packingCharges,
          installAndServiceCharges: updatedFormData.installationServiceCharges,
          projectLeadTime: updatedFormData.projectLeadTime,
          itemCostOthCharges: updatedFormData.otherCharges,
          grandTotalTax: updatedFormData.grandTotalTax,
          totalAmount: updatedFormData.totalAmount,
          grandTotal: updatedFormData.grandTotal,
          amendmentRevisionNumber: '',
          amendmentChangeDate: '',
          amendmentReason: '',
          approvedDate: '',
          approvedBy: updatedFormData.approvedBy,
          signedBy: updatedFormData.signedBy,
          poType: updatedFormData.poType,
          priority: 'Normal',
          sourceType: 'Manual',
          vendorQuoteNo: updatedFormData.vendorQuoteNumber,
          vendorQuoteDate: updatedFormData.vendorQuoteDate,
          isActive: true,
          branchId: selectedBranch || 0, // Use selected branch from header
          shipToBranchId: parseInt(updatedFormData.shipToBranchId),
          createdBy: user?.id,
          updatedBy: user?.id,
          supplierId: parseInt(updatedFormData.supplier)
        };

        // console.log('Submitting PO for approval with data:', poHeaderData);
        // console.log('updatedFormData.shipToBranchId:', updatedFormData.shipToBranchId);
        // console.log('branchId:', parseInt(updatedFormData.shipToBranchId));

        const createdPO = await purchaseOrderApiService.createPurchaseOrder(poHeaderData);

        // Create PO items
        for (const item of updatedFormData.lineItems) {
          const poItemData: PoItemDetailCreateRequest = {
            quantity: item.quantity,
            receivedQuantity: 0,
            unitOfMeasure: item.unit,
            itemDescription: item.itemName,
            unitPrice: item.purchaseRate,
            discountPercentage: 0,
            discountAmount: 0,
            taxPercentage: item.igst + item.cgst + item.sgst,
            taxAmount: item.totalTax,
            totalAmount: item.amount,
            notes: '',
            poHeaderId: createdPO.id,
            rawItemId: parseInt(item.rawItemId)
          };

          await purchaseOrderApiService.createPoItemDetail(poItemData);
        }

        setFormData(updatedFormData);
        toast.success('Purchase order submitted for approval!');
        navigate('/purchase-orders');
      }
    } catch (error) {
      // console.error('Error submitting purchase order:', error);
      toast.error('Failed to submit purchase order for approval');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      poNumber: '',
      shipToBranchId: '',
      purchaseDate: getCurrentDate(),
      poType: '',
      effectiveStartDate: getCurrentDate(),
      effectiveEndDate: getFutureDate(30),
      vendorQuoteNumber: '',
      vendorQuoteDate: '',
      supplier: '',
      modeOfDispatch: '',
      transportationCharges: 0,
      insuranceCharges: 0,
      packingCharges: 0,
      installationServiceCharges: 0,
      projectLeadTime: '',
      otherCharges: 0,
      grandTotalTax: 0,
      totalAmount: 0,
      grandTotal: 0,
      deliveryTerms: '',
      termConditions: '',
      paymentTerms: '',
      currency: 'INR',
      lineItems: [],
      status: 'Draft',
      approvedBy: null,
      signedBy: null
    });
    toast.info('Form cleared');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Draft': return 'bg-gray-100 text-gray-700';
      case 'Pending Approval': return 'bg-yellow-100 text-yellow-700';
      case 'Approved': return 'bg-green-100 text-green-700';
      case 'Rejected': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  // Helper function to determine if fields should be disabled
  const isFieldDisabled = (fieldName: string) => {
    if (isViewMode) return true;
    if (isEditMode && ['poNumber'].includes(fieldName)) return true;
    return false;
  };

  // Check if Submit for Approval button should be disabled
  const isSubmitDisabled = () => {
    // Must have PO Type selected
    if (!formData.poType) return true;
    
    // Must have Supplier selected
    if (!formData.supplier) return true;
    
    // Must have at least one line item
    if (formData.lineItems.length === 0) return true;
    
    // Total amount must not be 0
    if (formData.totalAmount === 0) return true;
    
    // If loading, disable the button
    if (loading) return true;
    
    return false;
  };

  const isViewOrEdit = isEditMode || isViewMode;

  if (isViewOrEdit && poLoadState === 'loading') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary-600 mx-auto" />
          <p className="mt-4 text-gray-600">Loading purchase order...</p>
        </div>
      </div>
    );
  }

  if (isViewOrEdit && poLoadState === 'not_found') {
    return (
      <div className="container mx-auto p-6 space-y-4">
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate('/purchase-orders')}
          className="flex items-center gap-2 text-primary-600 border-primary-200 hover:bg-primary-50"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Purchase Orders
        </Button>
        <Card className="border-primary-200 shadow-md">
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-lg font-medium text-gray-700">Purchase order not found</p>
            <p className="text-sm text-gray-500 mt-2">
              This purchase order does not exist or is not available for the selected branch.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
    <Card className="w-full border-0 shadow-lg bg-white/70 backdrop-blur-sm">
      <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-t-lg">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Purchase Order Details</CardTitle>
            <CardDescription className="text-primary-100">
              Create and manage purchase orders with vendor and item details
            </CardDescription>
          </div>
          <Badge className={`${getStatusColor(formData.status)} border-0`}>
            {formData.status}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="p-6">
        {/* Back Button */}
        <div className="mb-6">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/purchase-orders')}
            className="flex items-center gap-2 text-primary-600 border-primary-200 hover:bg-primary-50"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Purchase Orders
          </Button>
        </div>


        <form onSubmit={handleSave} className="space-y-8">
          {/* Order Information */}
          <div className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center shadow-lg">
                  <Calendar className="h-4 w-4 text-white" />
                </div>
                <h3 className="text-lg font-semibold bg-gradient-to-r from-primary-700 to-primary-800 bg-clip-text text-transparent">
                  {isViewMode ? 'View Purchase Order' : isEditMode ? 'Edit Purchase Order' : 'Create New Purchase Order'}
                </h3>
              </div>
              <Separator className="bg-gradient-to-r from-primary-200 to-primary-300 h-0.5" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Only show PO Number when editing or viewing */}
              {!isNewMode && (
                <div className="space-y-2">
                  <Label htmlFor="poNumber" className="text-gray-700">PO Number</Label>
                  <Input
                    id="poNumber"
                    value={formData.poNumber}
                    onChange={(e) => handleInputChange('poNumber', e.target.value)}
                    placeholder="Auto-generated"
                    className="border-primary-200 focus:border-primary-400 focus:ring-primary-400 bg-gradient-to-r from-primary-50/30 to-white"
                    readOnly={isFieldDisabled('poNumber')}
                    disabled={isFieldDisabled('poNumber')}
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="shipToBranchId" className="text-gray-700">Ship To</Label>
                {(isNewMode || isEditMode) ? (
                  <div className="p-2 bg-gray-50 rounded-md border border-primary-200">
                    <span className="font-medium text-gray-800">
                      {(() => {
                        const selected = shipToOptions.find(b => b.id.toString() === formData.shipToBranchId);
                        return selected ? `${selected.branchCode} - ${selected.branchName}` : 'Select Branch in header';
                      })()}
                    </span>
                  </div>
                ) : (
                  <select 
                    value={formData.shipToBranchId} 
                    onChange={(e) => handleInputChange('shipToBranchId', e.target.value)}
                    disabled={isFieldDisabled('shipToBranchId') || loadingBranches}
                    className="w-full px-3 py-2 border border-primary-200 rounded-md focus:border-primary-400 focus:ring-primary-400"
                  >
                    <option value="">{loadingBranches ? "Loading branches..." : "Select shipping destination"}</option>
                    {shipToOptions.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.branchName} - {option.branchCode}
                      </option>
                    ))}
                  </select>
                )}
                

                
                {/* Tax System Preview */}
                {formData.shipToBranchId && formData.supplier && (
                  <div className="text-xs text-gray-500 bg-blue-50 p-2 rounded-md border border-blue-200">
                    <div className="flex items-center gap-1">
                      <Info className="h-3 w-3 text-blue-600" />
                      <span>
                        {isSameState() 
                          ? 'Same state as supplier - CGST + SGST rates from HSN codes'
                          : 'Different state from supplier - IGST rates from HSN codes'
                        }
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="purchaseDate" className="text-gray-700">Purchase Date</Label>
                <Input
                  id="purchaseDate"
                  type="date"
                  value={formData.purchaseDate}
                  readOnly
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400 bg-gray-50"
                  disabled={true}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="poType" className="text-gray-700">
                  PO Type <span className="text-red-500">*</span>
                </Label>
                <Select value={formData.poType} onValueChange={(value) => handleInputChange('poType', value)} disabled={isFieldDisabled('poType')}>
                  <SelectTrigger className="border-primary-200 focus:border-primary-400 focus:ring-primary-400" >
                    <SelectValue placeholder="Select PO type" />
                  </SelectTrigger>
                  <SelectContentWithSearch searchPlaceholder="Search...">
                    {poTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContentWithSearch>
                </Select>
              </div>

              {/* Status dropdown - only show in edit mode and for users with approve permission */}
              {/* Commented out for now - status change feature */}
              {/* {isEditMode && (
                <PermissionGate route="/purchase-orders" action="approve">
                  <div className="space-y-2">
                    <Label htmlFor="status" className="text-gray-700">
                      Status
                    </Label>
                    <div className="flex items-center gap-2">
                      <Select value={formData.status} onValueChange={(value) => handleInputChange('status', value)} disabled={isFieldDisabled('status') || statusLoading}>
                        <SelectTrigger className="border-primary-200 focus:border-primary-400 focus:ring-primary-400 flex-1" >
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          {poStatuses.filter(status => status !== 'DRAFT').map((status) => (
                            <SelectItem key={status} value={status}>
                              {status}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {formData.status !== originalStatus && (
                        <>
                          <Button
                            type="button"
                            size="icon"
                            variant="outline"
                            onClick={handleSaveStatus}
                            disabled={statusLoading}
                            className="h-9 w-9 border-green-500 text-green-600 hover:bg-green-50"
                            title="Save status"
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            size="icon"
                            variant="outline"
                            onClick={handleCancelStatus}
                            disabled={statusLoading}
                            className="h-9 w-9 border-red-500 text-red-600 hover:bg-red-50"
                            title="Cancel status change"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </PermissionGate>
              )} */}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label htmlFor="effectiveStartDate" className="text-gray-700">Effective Start Date</Label>
                <Input
                  id="effectiveStartDate"
                  type="date"
                  value={formData.effectiveStartDate}
                  onChange={(e) => handleInputChange('effectiveStartDate', e.target.value)}
                  min={isNewMode ? getCurrentDate() : undefined}
                  max={isNewMode ? getFutureDate(7) : undefined}
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  disabled={isFieldDisabled('effectiveStartDate')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="effectiveEndDate" className="text-gray-700">Effective End Date</Label>
                <Input
                  id="effectiveEndDate"
                  type="date"
                  value={formData.effectiveEndDate}
                  onChange={(e) => handleInputChange('effectiveEndDate', e.target.value)}
                  min={isNewMode ? getCurrentDate() : undefined}
                  max="2099-12-31"
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  disabled={isFieldDisabled('effectiveEndDate')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="currency" className="text-gray-700">Currency</Label>
                <Select
                  value={formData.currency || 'INR'}
                  onValueChange={(value) => handleInputChange('currency', value)}
                  disabled={isFieldDisabled('currency')}
                >
                  <SelectTrigger
                    id="currency"
                    className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  >
                    <SelectValue placeholder="Select currency" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(CURRENCY_CONFIG).map(([code, config]) => (
                      <SelectItem key={code} value={code}>
                        {code} ({config.symbol})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Vendor Information */}
          <div className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center shadow-lg">
                  <Building className="h-4 w-4 text-white" />
                </div>
                <h3 className="text-lg font-semibold bg-gradient-to-r from-primary-700 to-primary-800 bg-clip-text text-transparent">
                  Vendor Information
                </h3>
              </div>
              <Separator className="bg-gradient-to-r from-primary-200 to-primary-300 h-0.5" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label htmlFor="supplier" className="text-gray-700">
                  Supplier <span className="text-red-500">*</span>
                </Label>
                <Select value={formData.supplier} onValueChange={(value) => handleInputChange('supplier', value)} disabled={loadingSuppliers || isFieldDisabled('supplier')}>
                  <SelectTrigger className="w-full border-primary-200 focus:border-primary-400 focus:ring-primary-400" >
                    <SelectValue placeholder={loadingSuppliers ? "Loading suppliers..." : "Select supplier"}>
                      {formData.supplier ? (() => {
                        const s = suppliers.find(supplier => supplier.id.toString() === formData.supplier);
                        if (!s) return undefined;
                        const extra = [s.gstNo, s.stateName].filter(Boolean).join(' • ');
                        return extra ? `${s.supplierName} (${extra})` : s.supplierName;
                      })() : undefined}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContentWithSearch searchPlaceholder="Search suppliers..." className="z-[9999] max-h-60">
                    {suppliers.length > 0 ? (
                      suppliers.map((supplier) => (
                        <SelectItem key={supplier.id} value={supplier.id.toString()}>
                          <span className="flex items-center gap-1.5 flex-wrap">
                            <span>{supplier.supplierName}</span>
                            {supplier.gstNo && (
                              <>
                                <span className="text-gray-500">|</span>
                                <span className="text-xs text-gray-500">{supplier.gstNo}</span>
                              </>
                            )}
                            {supplier.stateName && (
                              <>
                                <span className="text-gray-500">|</span>
                                <span className="text-xs text-gray-500">{supplier.stateName}</span>
                              </>
                            )}
                          </span>
                        </SelectItem>
                      ))
                    ) : (
                      <div className="px-2 py-1 text-sm text-gray-500">
                        {loadingSuppliers ? 'Loading suppliers...' : 'No suppliers found'}
                      </div>
                    )}
                  </SelectContentWithSearch>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="vendorQuoteNumber" className="text-gray-700">Vendor Quote Number</Label>
                <Input
                  id="vendorQuoteNumber"
                  value={formData.vendorQuoteNumber}
                  onChange={(e) => handleInputChange('vendorQuoteNumber', e.target.value)}
                  placeholder="Enter vendor quote number"
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="vendorQuoteDate" className="text-gray-700">Vendor Quote Date</Label>
                <Input
                  id="vendorQuoteDate"
                  type="date"
                  value={formData.vendorQuoteDate}
                  onChange={(e) => handleInputChange('vendorQuoteDate', e.target.value)}
                  max={getCurrentDate()}
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  disabled={isFieldDisabled('vendorQuoteDate')}
                />
              </div>
            </div>

            {/* Tax System Indicator - full width below the row */}
            {formData.supplier && formData.shipToBranchId && (
              <div className="text-sm text-gray-600 bg-gray-50 p-2 rounded-md border border-gray-200 mt-4">
                <div className="flex items-center gap-2">
                  <Info className="h-4 w-4 text-blue-600" />
                  <span>
                    <strong>Tax System:</strong> {isSameState() ? 'CGST + SGST (Same State)' : 'IGST (Inter-State)'}
                  </span>
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {isSameState() 
                    ? 'Supplier and shipping destination are in the same state. Tax rates will be based on HSN codes.'
                    : 'Supplier and shipping destination are in different states. Tax rates will be based on HSN codes.'
                  }
                </div>
                <div className="text-xs text-gray-400 mt-1 italic">
                  Note: Individual item tax rates are automatically calculated from their HSN codes
                </div>
              </div>
            )}
          </div>

          

          {/* Line Items Section */}
          <div className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center shadow-lg">
                    <Calculator className="h-4 w-4 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold bg-gradient-to-r from-primary-700 to-primary-800 bg-clip-text text-transparent">
                    Line Items
                  </h3>
                  {!isViewMode && (
                    <Button
                      type="button"
                      onClick={addLineItem}
                      className="ml-4 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white shadow-lg"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Item
                    </Button>
                  )}
                </div>
              </div>
              <Separator className="bg-gradient-to-r from-primary-200 to-primary-300 h-0.5" />
            </div>

            {formData.lineItems.length > 0 ? (
              <div className="border border-primary-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                        <TableHead className="w-36">Category</TableHead>
                        <TableHead className="w-44">Item Name</TableHead>
                        <TableHead className="w-36 min-w-[140px]">Rate (₹)</TableHead>
                        <TableHead className="w-32 min-w-[120px]">Qty</TableHead>
                        <TableHead className="w-20">HSN</TableHead>
                        <TableHead className="w-16">Unit</TableHead>
                        {!isSameState() && <TableHead className="w-20">IGST%</TableHead>}
                        <TableHead className="w-20">CGST%</TableHead>
                        <TableHead className="w-20">SGST%</TableHead>
                        <TableHead className="w-28">Total Tax (₹)</TableHead>
                        <TableHead className="w-28">Amount (₹)</TableHead>
                        {!isViewMode && <TableHead className="w-20">Actions</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {formData.lineItems.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>
                            {isEditMode && !item.id.startsWith('line_') && !isNaN(parseInt(item.id)) ? (
                              <div className="font-medium">{item.category || '-'}</div>
                            ) : (
                              <Select 
                                value={item.category} 
                                onValueChange={(value) => handleCategoryChange(item.id, value)}
                                disabled={isViewMode || loadingCategories}
                              >
                                <SelectTrigger className="border-primary-200 focus:border-primary-400 w-full" >
                                  <SelectValue placeholder={loadingCategories ? "Loading..." : "Select"} />
                                </SelectTrigger>
                                <SelectContentWithSearch searchPlaceholder="Search...">
                                  {categories.map((category) => (
                                    <SelectItem key={category.id} value={category.categoryName}>
                                      {category.categoryName}
                                    </SelectItem>
                                  ))}
                                </SelectContentWithSearch>
                              </Select>
                            )}
                          </TableCell>
                          <TableCell>
                            {isEditMode && !item.id.startsWith('line_') && !isNaN(parseInt(item.id)) ? (
                              <div className="font-medium">{item.itemName || '-'}</div>
                            ) : (
                              <>
                                <div className="flex items-center gap-2">
                                  <Select 
                                    value={item.rawItemId || ''} 
                                    onValueChange={(value) => selectItemForLine(item.id, value)}
                                    disabled={isViewMode || loadingRawItems || !item.category}
                                  >
                                    <SelectTrigger className={`border-primary-200 focus:border-primary-400 flex-1 ${!item.rawItemId ? 'border-red-400 bg-red-50' : ''}`}>
                                      <SelectValue placeholder={!item.category ? "Select category first" : (loadingRawItems ? "Loading..." : "Select item")} />
                                    </SelectTrigger>
                                    <SelectContentWithSearch searchPlaceholder="Search...">
                                      {getFilteredRawItems(item.category, item.id).length === 0 ? (
                                        <div className="px-2 py-1.5 text-sm text-gray-500 text-center">
                                          <div className="text-gray-500 mb-1">No available items</div>
                                          <div className="text-xs text-gray-400">All items in this category are already selected</div>
                                        </div>
                                      ) : (
                                        getFilteredRawItems(item.category, item.id).map((availableItem) => (
                                          <SelectItem key={availableItem.id} value={availableItem.id.toString()}>
                                            {availableItem.itemName}
                                          </SelectItem>
                                        ))
                                      )}
                                    </SelectContentWithSearch>
                                  </Select>
                                  {item.rawItemId && (
                                    <button
                                      type="button"
                                      onClick={() => handleShowTransactions(item.rawItemId, item.itemName)}
                                      className="flex-shrink-0 p-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors"
                                      title="View last 5 transactions"
                                    >
                                      <Info className="h-4 w-4" />
                                    </button>
                                  )}
                                </div>
                                {!item.rawItemId && (
                                  <div className="text-xs text-red-500 mt-1">⚠️ Please select an item</div>
                                )}
                              </>
                            )}
                          </TableCell>
                          <TableCell>
                            <Input
                              type="text"
                              value={rawInputs[item.id]?.rate ?? (item.purchaseRate?.toString() || '0')}
                              onChange={(e) => {
                                const val = e.target.value;
                                setRawInputs(prev => ({ ...prev, [item.id]: { ...prev[item.id], rate: val } }));
                                const numVal = parseFloat(val);
                                if (!isNaN(numVal)) {
                                  updateLineItem(item.id, 'purchaseRate', numVal);
                                }
                              }}
                              onBlur={(e) => {
                                const val = parseFloat(e.target.value) || 0;
                                updateLineItem(item.id, 'purchaseRate', val);
                                setRawInputs(prev => {
                                  const newState = { ...prev };
                                  delete newState[item.id]?.rate;
                                  return newState;
                                });
                              }}
                              onFocus={(e) => e.target.select()}
                              className="border-2 border-primary-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 w-full font-semibold text-base bg-primary-50/50 py-2.5"
                              placeholder="0.00"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="text"
                              value={rawInputs[item.id]?.qty ?? (item.quantity?.toString() || '0')}
                              onChange={(e) => {
                                const val = e.target.value;
                                setRawInputs(prev => ({ ...prev, [item.id]: { ...prev[item.id], qty: val } }));
                                const numVal = parseFloat(val);
                                if (!isNaN(numVal)) {
                                  updateLineItem(item.id, 'quantity', numVal);
                                }
                              }}
                              onBlur={(e) => {
                                const val = parseFloat(e.target.value) || 0;
                                updateLineItem(item.id, 'quantity', val);
                                setRawInputs(prev => {
                                  const newState = { ...prev };
                                  delete newState[item.id]?.qty;
                                  return newState;
                                });
                              }}
                              onFocus={(e) => e.target.select()}
                              className="border-2 border-blue-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 w-full font-semibold text-lg bg-blue-50/50 py-2.5"
                              placeholder="0"
                            />
                          </TableCell>
                          <TableCell>
                            <div className="font-medium">{item.hsn || '-'}</div>
                          </TableCell>
                          <TableCell>
                            <div className="font-medium">{item.unit || '-'}</div>
                          </TableCell>
                          {!isSameState() && (
                            <TableCell>
                              <div className="font-medium">{item.igst ? item.igst.toFixed(2) + '%' : '0.00%'}</div>
                            </TableCell>
                          )}
                          <TableCell>
                            <div className="font-medium">{item.cgst ? item.cgst.toFixed(2) + '%' : '0.00%'}</div>
                          </TableCell>
                          <TableCell>
                            <div className="font-medium">{item.sgst ? item.sgst.toFixed(2) + '%' : '0.00%'}</div>
                          </TableCell>
                          <TableCell>
                            <div className="font-medium">₹{item.totalTax.toFixed(2)}</div>
                          </TableCell>
                          <TableCell>
                            <div className="font-medium">₹{item.amount.toFixed(2)}</div>
                          </TableCell>
                          {!isViewMode && (
                            <TableCell>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => removeLineItem(item.id)}
                                className="border-red-200 hover:bg-red-50 text-red-600"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 bg-gradient-to-r from-primary-50/30 to-white rounded-lg border border-primary-200">
                <Calculator className="h-12 w-12 mx-auto mb-3 text-primary-300" />
                <p>No line items added yet. Click "Add Item" to get started.</p>
              </div>
            )}
            
            {/* Add Item Button at Bottom */}
            {!isViewMode && (
              <div className="flex justify-start">
                <Button
                  type="button"
                  onClick={addLineItem}
                  className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white shadow-lg"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Item
                </Button>
              </div>
            )}
          </div>

          {/* Shipping & Charges */}
          <div className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center shadow-lg">
                  <Truck className="h-4 w-4 text-white" />
                </div>
                <h3 className="text-lg font-semibold bg-gradient-to-r from-primary-700 to-primary-800 bg-clip-text text-transparent">
                  Shipping & Charges
                </h3>
              </div>
              <Separator className="bg-gradient-to-r from-primary-200 to-primary-300 h-0.5" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label htmlFor="modeOfDispatch" className="text-gray-700">Mode of Dispatch</Label>
                <Select value={formData.modeOfDispatch} onValueChange={(value) => handleInputChange('modeOfDispatch', value)} >
                  <SelectTrigger className="border-primary-200 focus:border-primary-400 focus:ring-primary-400" >
                    <SelectValue placeholder="Select mode of dispatch" />
                  </SelectTrigger>
                  <SelectContentWithSearch searchPlaceholder="Search...">
                    {modeOfDispatchOptions.map((mode) => (
                      <SelectItem key={mode} value={mode}>
                        {mode}
                      </SelectItem>
                    ))}
                  </SelectContentWithSearch>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="transportationCharges" className="text-gray-700">Transportation Charges (₹)</Label>
                <Input
                  id="transportationCharges"
                  value={formData.transportationCharges || ''}
                  onChange={(e) => handleInputChange('transportationCharges', parseFloat(e.target.value) || 0)}
                  onFocus={(e) => e.target.select()}
                  placeholder="0.00"
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="insuranceCharges" className="text-gray-700">Insurance Charges (₹)</Label>
                <Input
                  id="insuranceCharges"
                  value={formData.insuranceCharges || ''}
                  onChange={(e) => handleInputChange('insuranceCharges', parseFloat(e.target.value) || 0)}
                  onFocus={(e) => e.target.select()}
                  placeholder="0.00"
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="packingCharges" className="text-gray-700">Packing Charges (₹)</Label>
                <Input
                  id="packingCharges"
                  value={formData.packingCharges || ''}
                  onChange={(e) => handleInputChange('packingCharges', parseFloat(e.target.value) || 0)}
                  onFocus={(e) => e.target.select()}
                  placeholder="0.00"
                  className="border-primary-400 focus:ring-primary-400"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="installationServiceCharges" className="text-gray-700">Installation & Service Charges (₹)</Label>
                <Input
                  id="installationServiceCharges"
                  value={formData.installationServiceCharges || ''}
                  onChange={(e) => handleInputChange('installationServiceCharges', parseFloat(e.target.value) || 0)}
                  onFocus={(e) => e.target.select()}
                  placeholder="0.00"
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="projectLeadTime" className="text-gray-700">Project Lead Time</Label>
                <Input
                  id="projectLeadTime"
                  value={formData.projectLeadTime || calculateProjectLeadTime(formData.effectiveStartDate, formData.effectiveEndDate)}
                  readOnly
                  className="border-primary-200 bg-gray-50 font-medium text-gray-700 cursor-not-allowed"
                  placeholder="Auto-calculated from dates"
                />
                <p className="text-xs text-gray-500">
                  Calculated from: {formData.effectiveStartDate || 'Start Date'} to {formData.effectiveEndDate || 'End Date'}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="otherCharges" className="text-gray-700">Other Charges (₹)</Label>
                <Input
                  id="otherCharges"
                  value={formData.otherCharges}
                  onChange={(e) => handleInputChange('otherCharges', parseFloat(e.target.value) || 0)}
                  placeholder="0.00"
                  className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                />
              </div>
            </div>
          </div>

          {/* Order Summary */}
          {formData.lineItems.length > 0 && (
            <div className="space-y-6">
              <div className="flex justify-end">
                <div className="w-80 space-y-3 bg-gradient-to-r from-primary-50/50 to-white p-4 rounded-lg border border-primary-200">
                  <div className="flex justify-between">
                    <span>Total Amount:</span>
                    <span>₹{formData.totalAmount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Grand Total Tax:</span>
                    <span>₹{formData.grandTotalTax.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Additional Charges:</span>
                    <span>₹{(formData.transportationCharges + formData.insuranceCharges + formData.packingCharges + formData.installationServiceCharges + formData.otherCharges).toFixed(2)}</span>
                  </div>
                  <Separator className="bg-primary-200" />
                  <div className="flex justify-between font-semibold text-lg">
                    <span>Grand Total:</span>
                    <span className="text-primary-700">₹{formData.grandTotal.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Terms & Conditions */}
          <div className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center shadow-lg">
                  <Truck className="h-4 w-4 text-white" />
                </div>
                <h3 className="text-lg font-semibold bg-gradient-to-r from-primary-700 to-primary-800 bg-clip-text text-transparent">
                  Delivery Terms & Payment Terms
                </h3>
              </div>
              <Separator className="bg-gradient-to-r from-primary-200 to-primary-300 h-0.5" />
            </div>

            <div className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="paymentTerms" className="text-gray-700">Payment Terms</Label>
                <Textarea
                  id="paymentTerms"
                  value={formData.paymentTerms}
                  onChange={(e) => handleInputChange('paymentTerms', e.target.value)}
                  placeholder="Enter payment terms"
                  rows={3}
                  className={`border-primary-200 focus:border-primary-400 focus:ring-primary-400 ${
                    isViewMode ? 'text-orange-600 font-semibold' : ''
                  }`}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="deliveryTerms" className="text-gray-700">Delivery Terms</Label>
                <Textarea
                  id="deliveryTerms"
                  value={formData.deliveryTerms}
                  onChange={(e) => handleInputChange('deliveryTerms', e.target.value)}
                  placeholder="Enter delivery terms (e.g., FOB Origin, FOB Destination, CIF, etc.)"
                  rows={4}
                  className={`border-primary-200 focus:border-primary-400 focus:ring-primary-400 ${
                    isViewMode ? 'text-orange-600 font-semibold' : ''
                  }`}
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between pt-6">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => navigate('/purchase-orders')} 
              className="border-primary-200 hover:bg-gradient-to-r hover:from-primary-50 hover:to-primary-100 hover:border-primary-300 hover:text-primary-700"
            >
              {isViewMode ? 'Back' : 'Cancel'}
            </Button>
            
            {!isViewMode && (
              <Button 
                type="submit" 
                disabled={loading}
                className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Saving...' : 'Save'}
              </Button>
            )}
          </div>
        </form>
      </CardContent>
    </Card>

    {/* Transaction History Dialog */}
    <Dialog open={transactionDialogOpen} onOpenChange={setTransactionDialogOpen}>
      <DialogContent className="max-w-6xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Last 5 Transactions - {selectedItemName}</DialogTitle>
          <DialogDescription>
            Purchase order history for this item
          </DialogDescription>
        </DialogHeader>
        
        {loadingTransactions ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-gray-500">Loading transactions...</div>
          </div>
        ) : lastTransactions.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-gray-500">No transaction history found</div>
          </div>
        ) : (
          <div className="mt-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>PO Number</TableHead>
                  <TableHead>PO Date</TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead className="text-right">Rate (₹)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lastTransactions.map((transaction, index) => (
                  <TableRow key={index}>
                    <TableCell className="font-medium">{transaction.poNumber || '-'}</TableCell>
                    <TableCell>{transaction.poDate ? new Date(transaction.poDate).toLocaleDateString() : '-'}</TableCell>
                    <TableCell>{transaction.supplierName || '-'}</TableCell>
                    <TableCell className="text-right">{transaction.rate?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
}